import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useWishlist } from "../context/WishlistContext";
import { BaseUrl } from "../config/api";
import offerLogo from "../assets/images/offer-logo.svg";
import "../assets/styles/ProductDetail.css";
import {
  getRandomProducts,
  addToRecentlyViewed,
  getRecentlyViewedProducts,
} from "../utils/productUtils";
import ProductCard from "../components/ProductCard";
import { useCart } from "../context/CartContext";
import Cookies from "js-cookie";

const ProductDetail = () => {
  const { productId } = useParams();
  const [selectedSize, setSelectedSize] = useState("OneSize");
  const [selectedImage, setSelectedImage] = useState(0);
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const productInfoRef = useRef(null);
  const [activeSection, setActiveSection] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const { addToCart, isInCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${BaseUrl}/products/getproduct/${productId}`
        );
        if (!response.ok) {
          throw new Error("Product not found");
        }
        const data = await response.json();

        // Debug log to see raw data
        console.log("Raw data from API:", data);
        console.log("Actual Price from API:", data.actualPrice);
        console.log("Selling Price from API:", data.sellingPrice);

        // Transform the data to match your frontend structure
        const transformedProduct = {
          id: data._id,
          name: data.productName,
          // Explicitly parse as numbers and log the values
          actualPrice: parseFloat(data.actualPrice),
          sellingPrice: parseFloat(data.sellingPrice),
          price: `$${parseFloat(data.sellingPrice).toFixed(2)}`,
          images:
            data.images && data.images.length > 0
              ? data.images
              : [data.categoryId?.categotyImage],
          fabric: data.materialAndCare || "Not specified",
          color: data.color || "Not specified",
          size: data.sizeAndFit || "FREE SIZE",
          type: data.categoryName,
          dateAdded: new Date().toISOString(),
          discount: calculateDiscount(
            parseFloat(data.actualPrice),
            parseFloat(data.sellingPrice)
          ),
          description: data.description || "",
          stock: Number(data.stock || 0),
          availability: data.availability,
          productCode: data.productCode,
          tags: data.tags || [],
          tax: Number(data.tax || 0),
          couponCode: data.couponCode,
          couponMethod: data.couponMethod,
          variantName: data.variantName || "",
          categoryId: data.categoryId?._id,
          categoryType: data.categoryId?.categoryType,
          productCategory: data.categoryName,
          materialAndCare: data.materialAndCare || "Not specified",
          sareeSize: data.sareeSize ? `${data.sareeSize} meters` : null,
          blouseSize: data.blouseSize ? `${data.blouseSize} meters` : null,
          sizeAndFit: data.sizeAndFit || "FREE SIZE",
        };

        // Debug log transformed data
        console.log("Transformed product:", transformedProduct);
        console.log(
          "Transformed actual price:",
          transformedProduct.actualPrice
        );
        console.log(
          "Transformed selling price:",
          transformedProduct.sellingPrice
        );

        setProduct(transformedProduct);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching product details:", error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [productId]);

  useEffect(() => {
    if (product) {
      setIsWishlisted(isInWishlist(product.id));

      // Add product to recently viewed
      addToRecentlyViewed(product.id);

      // Fetch recently viewed products
      const fetchRecentlyViewed = async () => {
        try {
          const recentlyViewedIds = JSON.parse(
            Cookies.get("recentlyViewed") || "[]"
          );
          const response = await fetch(`${BaseUrl}/products/getallproducts`);
          const allProducts = await response.json();

          const recentProducts = recentlyViewedIds
            .map((id) => allProducts.find((p) => p._id === id))
            .filter((p) => p && p._id !== product.id)
            .map(transformProductData)
            .slice(0, 5);

          setRecentlyViewed(recentProducts);
        } catch (error) {
          console.error("Error fetching recently viewed products:", error);
          setRecentlyViewed([]);
        }
      };

      fetchRecentlyViewed();

      // Fetch similar products from the same category
      const fetchSimilarProducts = async () => {
        try {
          const response = await fetch(`${BaseUrl}/products/getallproducts`);
          const data = await response.json();
          const filtered = data
            .filter(
              (p) =>
                p.categoryName === product.type &&
                p._id !== product.id &&
                !p.isDeleted
            )
            .slice(0, 5)
            .map((p) => ({
              id: p._id,
              _id: p._id,
              name: p.productName,
              price: `$${Number(p.sellingPrice).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
              color: p.color,
              images:
                p.images && p.images.length > 0
                  ? p.images
                  : [p.categoryId?.categotyImage],
              discount: calculateDiscount(p.actualPrice, p.sellingPrice),
            }));
          setSimilarProducts(filtered);
          console.log("Similar products:", filtered);
        } catch (error) {
          console.error("Error fetching similar products:", error);
        }
      };

      fetchSimilarProducts();
    }
  }, [product, isInWishlist]);

  useEffect(() => {
    if (product && product.variantId) {
      const fetchVariantProducts = async () => {
        try {
          const response = await fetch(`${BaseUrl}/products/getallproducts`);
          const data = await response.json();
          const variantProducts = data
            .filter(
              (p) =>
                p.variantId === product.variantId &&
                p._id !== product.id &&
                !p.isDeleted
            )
            .map(transformProductData);
          setSimilarProducts(variantProducts);
        } catch (error) {
          console.error("Error fetching variant products:", error);
        }
      };

      fetchVariantProducts();
    }
  }, [product]);

  useEffect(() => {
    if (product && product.variantName) {
      const fetchSimilarProducts = async () => {
        try {
          const response = await fetch(`${BaseUrl}/products/getallproducts`);
          const data = await response.json();
          const similarVariants = data
            .filter(
              (p) =>
                p.variantName === product.variantName &&
                p._id !== product._id &&
                !p.isDeleted
            )
            .map((p) => ({
              id: p._id,
              _id: p._id,
              name: p.productName,
              price: `$${p.sellingPrice.toLocaleString()}`,
              color: p.color,
              images:
                p.images && p.images.length > 0
                  ? p.images
                  : [p.categoryId?.categotyImage],
              discount: calculateDiscount(p.actualPrice, p.sellingPrice),
            }));
          setSimilarProducts(similarVariants);
        } catch (error) {
          console.error("Error fetching similar products:", error);
        }
      };

      fetchSimilarProducts();
    }
  }, [product]);

  const calculateDiscount = (actualPrice, sellingPrice) => {
    // Debug log the inputs
    console.log("Calculating discount with:", { actualPrice, sellingPrice });

    const actual = parseFloat(actualPrice);
    const selling = parseFloat(sellingPrice);

    if (isNaN(actual) || isNaN(selling) || actual <= 0) {
      console.log("Invalid price values for discount calculation");
      return 0;
    }

    const discount = ((actual - selling) / actual) * 100;
    const roundedDiscount = Math.round(discount);

    // Debug log the result
    console.log("Calculated discount:", roundedDiscount);

    return roundedDiscount;
  };

  const transformProductData = (data) => ({
    id: data._id || data.id,
    name: data.productName,
    price: `$${Number(data.sellingPrice || 0).toFixed(2)}`,
    images:
      data.images && data.images.length > 0
        ? data.images
        : [data.categoryId?.categotyImage],
    type: data.categoryName,
    discount: calculateDiscount(
      Number(data.actualPrice || 0),
      Number(data.sellingPrice || 0)
    ),
    _id: data._id,
    // Add these for calculations
    actualPrice: Number(data.actualPrice || 0),
    sellingPrice: Number(data.sellingPrice || 0),
  });

  const formatCategoryName = (name) => {
    return name
      ? name
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : "";
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error || !product) {
    return (
      <div className="product-detail-container">
        <div className="breadcrumb">
          <Link to="/">Home</Link>
        </div>
        <div className="product-not-found">
          <h2>Product not found</h2>
          <Link to="/" className="back-home">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const productImages =
    product.images || [product.image, product.image2].filter(Boolean);
  const productOffers = product.offers || [];
  const categoryPath = product.categoryPath || "";

  const handleWishlist = (e) => {
    e.preventDefault();

    if (isWishlisted) {
      removeFromWishlist(parseInt(productId));
      setIsWishlisted(false);
    } else {
      addToWishlist(product);
      setIsWishlisted(true);
    }
  };

  const handlePrevImage = () => {
    setSelectedImage((prev) =>
      prev === 0 ? productImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImage((prev) =>
      prev === productImages.length - 1 ? 0 : prev + 1
    );
  };

  const formatPrice = (priceStr) => {
    const numericPrice = parseFloat(priceStr.replace("$", "").replace(",", ""));
    return `$${numericPrice.toLocaleString()}`;
  };

  const calculateInflatedPrice = (price, discount) => {
    const numericPrice = parseFloat(price.replace("$", "").replace(",", ""));
    const inflatedPrice = Math.round(
      numericPrice + (numericPrice * discount) / 100
    );
    return `$${inflatedPrice.toLocaleString()}`;
  };

  const getOfferDetails = (product) => {
    const offers = [
      {
        title: "BANK OFFER",
        description: "10% Instant Discount on HDFC Bank Credit Cards",
      },
    ];

    if (product.discount) {
      offers.push({
        title: "ADDITIONAL FLAT 20% OFF",
        description: "Products Are Pre Discounted For You!",
      });
    }

    if (product.offer === "Buy1Get1") {
      offers.push({
        title: "BUY 2 GET 1 FREE",
        description: "Add 3 Products To Your Cart And Get 1 For Free!",
      });
    }

    offers.push({
      title: "SPECIAL PRICE",
      description: "Get extra ₹2000 off (price inclusive of discount)",
    });

    if (product.offer?.title && product.offer?.description) {
      offers.push({
        title: product.offer.title,
        description: product.offer.description,
      });
    }

    return offers;
  };

  const offerDetails = getOfferDetails(product);

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  return (
    <>
      <div className="product-detail-container">
        <div className="breadcrumb">
          <Link to="/">Home</Link> /{" "}
          {product.productCategory && (
            <>
              <Link
                to={`/category/${product.productCategory
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {formatCategoryName(product.productCategory)}
              </Link>{" "}
              /{" "}
            </>
          )}
          <span>{product.name}</span>
        </div>

        <div className="product-detail-content">
          <div className="product-images">
            <div className="thumbnail-list">
              {productImages.map((image, index) => (
                <div
                  key={index}
                  className={`thumbnail ${
                    selectedImage === index ? "active" : ""
                  }`}
                  onClick={() => setSelectedImage(index)}
                >
                  <img src={image} alt={`Product view ${index + 1}`} />
                </div>
              ))}
            </div>
            <div className="main-image">
              <button className="nav-button prev" onClick={handlePrevImage}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <img src={productImages[selectedImage]} alt={product.name} />
              <button className="nav-button next" onClick={handleNextImage}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
              <button className="share-button">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
              </button>
            </div>
          </div>

          <div className="product-detail-info" ref={productInfoRef}>
            <div className="product-header">
              <h1>{product.name}</h1>
            </div>
            <div className="price-section">
              <div className="price-details">
                <span className="mrp-label">Price</span>
                {product.discount > 0 ? (
                  <>
                    <span className="original-price">
                      ${parseFloat(product.actualPrice).toFixed(2)}
                    </span>
                    <span className="current-price">
                      ${parseFloat(product.sellingPrice).toFixed(2)}
                    </span>
                    <span className="discount">
                      (
                      {calculateDiscount(
                        product.actualPrice,
                        product.sellingPrice
                      )}
                      % OFF)
                    </span>
                  </>
                ) : (
                  <span className="current-price">
                    ${parseFloat(product.sellingPrice).toFixed(2)}
                  </span>
                )}
              </div>
              <div className="inclusive-text">Inclusive of All Taxes</div>
            </div>

            <div className="size-section">
              <h3>SIZE</h3>
              <div className="size-selector">
                <button className="size-button selected">
                  {product.sizeAndFit}
                </button>
              </div>
            </div>
            <div className="color-section">
              <h3>COLOR</h3>
              <div className="color-variants">
                {product.variantName && similarProducts.length > 0 ? (
                  <div className="variant-thumbnails">
                    {similarProducts.map((item) => (
                      <Link
                        key={item._id}
                        to={`/product/${item._id || item.id}`}
                        className="variant-thumbnail"
                        onClick={() => {
                          window.scrollTo(0, 0);
                          console.log("Product ID:", item._id || item.id);
                        }}
                      >
                        <img
                          src={item.images[0]}
                          alt={item.color || "Product variant"}
                        />
                        <span className="color-name">
                          {item.color || "Default"}
                        </span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="single-color">
                    <div className="color-thumbnail">
                      <img
                        src={productImages[0]}
                        alt={product.color || "Product color"}
                      />
                      <span className="color-name">
                        {product.color || "Default"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="action-buttons">
              <button
                className={`add-to-cart ${
                  isInCart(product.id) ? "in-cart" : ""
                }`}
                onClick={() => addToCart(product)}
                disabled={isInCart(product.id)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path d="M9 20a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
                  <path d="M20 20a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                {isInCart(product.id) ? "IN CART" : "ADD TO CART"}
              </button>
              <button
                className={`wishlist ${isWishlisted ? "wishlist-active" : ""}`}
                onClick={handleWishlist}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill={isWishlisted ? "currentColor" : "none"}
                  stroke="currentColor"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                {isWishlisted ? "WISHLISTED" : "WISHLIST"}
              </button>
            </div>
            <div className="product-details-info-hr-container">
              <hr />
            </div>
            <div className="product-details-section visible">
              <h2>Product Details</h2>
              <ul className="details-list">
                {product.description ? (
                  <li>{product.description}</li>
                ) : (
                  <li>Description not available</li>
                )}
              </ul>

              {/* Only show Tags section if tags exist and are not empty */}
              {product.tags && product.tags.length > 0 && (
                <div className="tags-section">
                  <h3>Tags</h3>
                  <div className="tags-list">
                    {product.tags.map((tag, index) => (
                      <span key={index} className="tag-item">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="material-care">
                {/* Only show Size & Fit for Saree category type */}
                {product.categoryType === "Saree" && (
                  <>
                    <h3>Size & Fit</h3>
                    <ul className="details-list">
                      <li>
                        {product.sareeSize && (
                          <span>Saree : {product.sareeSize} Mtrs </span>
                        )}
                        {product.blouseSize && (
                          <span>Blouse : {product.blouseSize} Mtrs</span>
                        )}
                        {!product.sareeSize && !product.blouseSize && (
                          <span>Free Size</span>
                        )}
                      </li>
                    </ul>
                  </>
                )}

                {/* Only show Material & Care section if it exists and is not "Not specified" */}
                {product.materialAndCare &&
                  product.materialAndCare !== "Not specified" && (
                    <>
                      <h3>Material & Care</h3>
                      <ul className="details-list">
                        {product.materialAndCare
                          .split(",")
                          .map((item, index) => (
                            <li key={index}>{item.trim()}</li>
                          ))}
                      </ul>
                    </>
                  )}

                <h3>Product Code</h3>
                <ul className="details-list">
                  <li>{product.productCode}</li>
                </ul>
              </div>
            </div>
            <div className="product-note">
              <p>
                Note: Product color may slightly vary due to photographic
                lighting sources or your monitor settings.
              </p>
            </div>
            {/* <div className="accordion-sections">
              <div
                className="accordion-item"
                onClick={() => toggleSection("specifications")}
              >
                <div className="accordion-header">
                  <span>PRODUCT SPECIFICATIONS</span>
                  <span
                    className={`arrow ${
                      activeSection === "specifications" ? "active" : ""
                    }`}
                  ></span>
                </div>
                {activeSection === "specifications" && (
                  <div className="accordion-content">
                    <ul>
                      <li>Product Type: Saree</li>
                      <li>Material: Art Silk</li>
                      <li>Color: Rani Pink</li>
                      <li>Pattern: Zariwork Embroidery</li>
                      <li>Length: 5.5 meters</li>
                      <li>Blouse Piece: Included (0.80 meters)</li>
                      <li>Occasion: Festival, Wedding, Party</li>
                      <li>
                        Package Contents: 1 Saree with Unstitched Blouse Piece
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              <div
                className="accordion-item"
                onClick={() => toggleSection("shipping")}
              >
                <div className="accordion-header">
                  <span>SHIPPING INFORMATION</span>
                  <span
                    className={`arrow ${
                      activeSection === "shipping" ? "active" : ""
                    }`}
                  ></span>
                </div>
                {activeSection === "shipping" && (
                  <div className="accordion-content">
                    <ul>
                      <li>Free shipping on all orders above ₹999</li>
                      <li>Delivery within 5-7 business days</li>
                      <li>
                        Express delivery available (additional charges apply)
                      </li>
                      <li>
                        Track your order through our website or mobile app
                      </li>
                      <li>Currently delivering to all major cities in India</li>
                      <li>
                        International shipping available to select countries
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              <div
                className="accordion-item"
                onClick={() => toggleSection("more")}
              >
                <div className="accordion-header">
                  <span>MORE INFORMATION</span>
                  <span
                    className={`arrow ${
                      activeSection === "more" ? "active" : ""
                    }`}
                  ></span>
                </div>
                {activeSection === "more" && (
                  <div className="accordion-content">
                    <ul>
                      <li>
                        Style Tips: Pair with traditional jewelry for a complete
                        look
                      </li>
                      <li>Maintenance: Dry clean only</li>
                      <li>Storage: Keep in a cool, dry place</li>
                      <li>Authenticity: 100% genuine product</li>
                      <li>
                        Quality Check: Passes through 3-step quality check
                      </li>
                      <li>
                        Certification: ISO 9001:2015 certified manufacturer
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              <div
                className="accordion-item"
                onClick={() => toggleSection("help")}
              >
                <div className="accordion-header">
                  <span>NEED HELP</span>
                  <span
                    className={`arrow ${
                      activeSection === "help" ? "active" : ""
                    }`}
                  ></span>
                </div>
                {activeSection === "help" && (
                  <div className="accordion-content">
                    <ul>
                      <li>Customer Care: 1800-123-4567 (Toll-Free)</li>
                      <li>Email: support@koskii.com</li>
                      <li>Working Hours: Mon-Sat, 10 AM - 7 PM</li>
                      <li>WhatsApp Support: +91 98765 43210</li>
                      <li>Live Chat: Available on website and app</li>
                    </ul>
                  </div>
                )}
              </div>

              <div
                className="accordion-item"
                onClick={() => toggleSection("offer")}
              >
                <div className="accordion-header">
                  <span>HOW TO AVAIL THE BUY 2 GET 1 OFFER</span>
                  <span
                    className={`arrow ${
                      activeSection === "offer" ? "active" : ""
                    }`}
                  ></span>
                </div>
                {activeSection === "offer" && (
                  <div className="accordion-content">
                    <ul>
                      <li>Add any 3 products to your cart</li>
                      <li>
                        Discount will be automatically applied at checkout
                      </li>
                      <li>Lowest priced item will be free</li>
                      <li>Offer valid on selected products only</li>
                      <li>Cannot be combined with other offers</li>
                      <li>Valid until stocks last</li>
                    </ul>
                  </div>
                )}
              </div>

              <div
                className="accordion-item"
                onClick={() => toggleSection("faq")}
              >
                <div className="accordion-header">
                  <span>FAQ'S</span>
                  <span
                    className={`arrow ${
                      activeSection === "faq" ? "active" : ""
                    }`}
                  ></span>
                </div>
                {activeSection === "faq" && (
                  <div className="accordion-content">
                    <div className="faq-item">
                      <h4>Is blouse piece included?</h4>
                      <p>
                        Yes, an unstitched blouse piece of 0.80 meters is
                        included.
                      </p>
                    </div>
                    <div className="faq-item">
                      <h4>Do you offer customization?</h4>
                      <p>
                        Yes, we offer blouse customization services at
                        additional cost.
                      </p>
                    </div>
                    <div className="faq-item">
                      <h4>What are the payment options?</h4>
                      <p>
                        We accept all major credit/debit cards, UPI, net
                        banking, and COD.
                      </p>
                    </div>
                    <div className="faq-item">
                      <h4>Is exchange available?</h4>
                      <p>
                        Yes, size exchange is available within 7 days of
                        delivery.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div
                className="accordion-item"
                onClick={() => toggleSection("return-policy")}
              >
                <div className="accordion-header">
                  <span>RETURN POLICY</span>
                  <span
                    className={`arrow ${
                      activeSection === "return-policy" ? "active" : ""
                    }`}
                  ></span>
                </div>
                {activeSection === "return-policy" && (
                  <div className="accordion-content">
                    <ul>
                      <li>Easy returns within 7 days of delivery</li>
                      <li>Product must be unused with original tags</li>
                      <li>Free pickup for returns</li>
                      <li>Refund processed within 5-7 business days</li>
                      <li>Exchange option available</li>
                      <li>For detailed policy, visit our returns page</li>
                    </ul>
                  </div>
                )}
              </div>
            </div> */}
            <div className="bottom-cards">
              <div className="card-item">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 7h-3V6a4 4 0 0 0-8 0v1H5a2 2 0 0 0-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM7 10.82C5.84 10.4 5 9.3 5 8V7h2v3.82zM19 8c0 1.3-.84 2.4-2 2.82V7h2v1z" />
                </svg>
                <span>PAY ON DELIVERY</span>
              </div>

              <div className="card-item">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 8h-9L8.47 3.63a.998.998 0 0 0-1.01-.44L3 4.02v16.97l4.46-.82A1 1 0 0 1 8 21h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm-1 9H8.82a3 3 0 0 0-.55.05l-3.27.6V6.72l2.69-.5L10.46 11H19v6z" />
                </svg>
                <span>FREE SHIPPING</span>
              </div>

              <div className="card-item">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM7 10.82C5.84 10.4 5 9.3 5 8V7h2v3.82zM19 8c0 1.3-.84 2.4-2 2.82V7h2v1z" />
                </svg>
                <span>TOP BRAND</span>
              </div>

              <div className="card-item">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
                </svg>
                <span>SECURE PAYMENT</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="hr-container">
        <hr />
      </div>
      {/* You may also like section */}
      <div className="related-products-section">
        <h2>You may also like</h2>
        <div className="products-slider">
          {similarProducts.map((product) => (
            <Link
              key={product.id}
              to={`/product/${product._id || product.id}`}
              className="product-card"
              onClick={() => {
                window.scrollTo(0, 0);
                console.log("Product ID:", product._id || product.id);
              }}
            >
              <div className="product-card-container">
                <div className="product-image-container">
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="primary-image"
                  />
                  {product.images[1] && (
                    <img
                      src={product.images[1]}
                      alt={product.name}
                      className="secondary-image"
                    />
                  )}
                  {product.discount > 0 && (
                    <span className="discount-tag">
                      {product.discount}% OFF
                    </span>
                  )}
                </div>
                <div className="product-info">
                  <h3>{product.name}</h3>
                  <p className="price">{product.price}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      {/* Recently Viewed section */}
      {recentlyViewed.length > 0 && (
        <div className="related-products-section">
          <h2>Recently Viewed</h2>
          <div className="products-slider">
            {recentlyViewed.map((product) => (
              <Link
                key={product.id}
                to={`/product/${product._id || product.id}`}
                className="product-card"
                onClick={() => {
                  window.scrollTo(0, 0);
                  console.log(
                    "Recently Viewed Product ID:",
                    product._id || product.id
                  );
                }}
              >
                <div className="product-card-container">
                  <div className="product-image-container">
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="primary-image"
                    />
                    {product.images[1] && (
                      <img
                        src={product.images[1]}
                        alt={product.name}
                        className="secondary-image"
                      />
                    )}
                    {product.discount > 0 && (
                      <span className="discount-tag">
                        {product.discount}% OFF
                      </span>
                    )}
                  </div>
                  <div className="product-info">
                    <h3>{product.name}</h3>
                    <p className="price">{product.price}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default ProductDetail;
